import React, { useState } from 'react';
import {
  Box, Container, Grid, Paper, Tab, Tabs, Typography, Table, TableHead,
  TableBody, TableRow, TableCell, Alert, Skeleton,
} from '@mui/material';
import { Forum, TrendingUp, Article, Insights } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { useUrlIntState } from '../hooks/useUrlState';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { redditAPI } from '../services/api';
import SectionTitle from './crt/SectionTitle';
import Panel from './crt/Panel';

const fmt = (n: number | null | undefined) => (n ?? 0).toLocaleString();

interface TabPanelProps { children?: React.ReactNode; index: number; value: number; }
const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div role="tabpanel" hidden={value !== index}>
    {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
  </div>
);

const RedditDashboard: React.FC = () => {
  const [active, setActive] = useUrlIntState('rdt_tab', 0);

  const { data: overview } = useQuery({ queryKey: ['reddit-overview'], queryFn: redditAPI.getOverview, staleTime: 60000 });
  const { data: history } = useQuery({ queryKey: ['reddit-history'], queryFn: () => redditAPI.getHistory(90), staleTime: 60000 });
  const { data: posts } = useQuery({ queryKey: ['reddit-posts'], queryFn: () => redditAPI.getPosts(50), staleTime: 60000 });
  const { data: traffic } = useQuery({ queryKey: ['reddit-traffic'], queryFn: () => redditAPI.getTraffic(90), staleTime: 60000 });

  if (overview && !overview.configured) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box mb={4}>
          <SectionTitle sub="&gt; Subreddit growth, engagement, top posts_">
            REDDIT :: COMMUNITY CONSOLE
          </SectionTitle>
        </Box>
        <Alert severity="info" sx={{ mb: 2 }}>
          Configure Reddit credentials to start syncing subreddit data.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box mb={4}>
        <SectionTitle sub="&gt; Subreddit growth, engagement, top posts_">
          REDDIT :: COMMUNITY CONSOLE
        </SectionTitle>
      </Box>

      {/* KPI row */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {[
          { label: 'SUBSCRIBERS', value: fmt(overview?.subscribers) },
          { label: 'ACTIVE USERS', value: fmt(overview?.active_users) },
          { label: 'TOTAL POSTS', value: fmt(overview?.total_posts) },
        ].map((kpi) => (
          <Grid size={{ xs: 12, md: 4 }} key={kpi.label}>
            <Panel label={kpi.label}>
              <Typography variant="h4" fontWeight={700}>{kpi.value}</Typography>
            </Panel>
          </Grid>
        ))}
      </Grid>

      <Paper sx={{ mb: 3 }}>
        <Tabs value={active} onChange={(_, v) => setActive(v)} variant="scrollable" scrollButtons="auto" sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab icon={<TrendingUp />} label="Growth" />
          <Tab icon={<Article />} label="Posts" />
          <Tab icon={<Insights />} label="Traffic" />
        </Tabs>
      </Paper>

      {/* Growth tab */}
      <TabPanel value={active} index={0}>
        <Panel label="SUBSCRIBER HISTORY">
          {history?.available ? (
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={history.daily} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip formatter={(v: number) => fmt(v)} />
                <Area type="monotone" dataKey="subscribers" stroke="#00ff41" fill="#00ff41" fillOpacity={0.2} name="Subscribers" />
              </AreaChart>
            </ResponsiveContainer>
          ) : <Skeleton variant="rectangular" height={300} />}
        </Panel>
      </TabPanel>

      {/* Posts tab */}
      <TabPanel value={active} index={1}>
        <Panel label="TOP POSTS BY SCORE">
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>TITLE</TableCell>
                  <TableCell>SUBREDDIT</TableCell>
                  <TableCell>AUTHOR</TableCell>
                  <TableCell align="right">SCORE</TableCell>
                  <TableCell align="right">UPVOTE %</TableCell>
                  <TableCell align="right">COMMENTS</TableCell>
                  <TableCell>POSTED</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(posts?.posts || []).map((p: any) => (
                  <TableRow key={p.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600} noWrap sx={{ maxWidth: 400 }}>{p.title}</Typography>
                    </TableCell>
                    <TableCell>{p.subreddit_name || '—'}</TableCell>
                    <TableCell>{p.author || '—'}</TableCell>
                    <TableCell align="right">{fmt(p.score)}</TableCell>
                    <TableCell align="right">{p.upvote_ratio ? `${(p.upvote_ratio * 100).toFixed(0)}%` : '—'}</TableCell>
                    <TableCell align="right">{fmt(p.num_comments)}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{p.created_utc ? new Date(p.created_utc).toLocaleDateString() : '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Panel>
      </TabPanel>

      {/* Traffic tab */}
      <TabPanel value={active} index={2}>
        {traffic?.available ? (
          <Panel label="DAILY TRAFFIC (PAGEVIEWS & UNIQUES)">
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={traffic.daily} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip formatter={(v: number) => fmt(v)} />
                <Area type="monotone" dataKey="pageviews" stroke="#00ff41" fill="#00ff41" fillOpacity={0.2} name="Pageviews" />
                <Area type="monotone" dataKey="uniques" stroke="#00ffff" fill="#00ffff" fillOpacity={0.1} name="Uniques" />
              </AreaChart>
            </ResponsiveContainer>
          </Panel>
        ) : (
          <Alert severity="info">
            Traffic data requires moderator access to the subreddit. If you are a moderator,
            traffic stats will appear here after the next sync.
          </Alert>
        )}
      </TabPanel>
    </Container>
  );
};

export default RedditDashboard;
